/**
 * Column definitions and totals-row builders for every report tab (spec
 * §6.8). Labels mirror the backend's own CSV/xlsx headers
 * (backend/internal/handlers/reports.go `dailyView` et al.) so what the
 * screen shows and what a downloaded file says line up exactly.
 *
 * Two formatting rules to keep straight, both pinned by the backend's own
 * comments (backend/internal/reports/queries.go):
 *   - `TaxBand.rate` is a **fraction** (0.18) — `percentLabel` multiplies by
 *     100 before rendering it.
 *   - `ProductRow.share` is **already a percentage** (42.5, not 0.425) —
 *     `plainPercentLabel` renders it as-is.
 */
import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import type { ReportColumn } from './ReportTable'
import { formatKgGrouped, formatMoney, signedMoney } from '@/lib/money'
import { formatBusinessDate, percentLabel, plainPercentLabel } from '@/lib/print/format'
import type {
  CashierRow, DailyRow, DayCloseRow, HourRow, PeriodSummary, ProductRow, ReceivableRow, TaxBand,
} from '@/types'

const DASH = '—'

/** `HH:00` for an hour-of-day bucket — shared with the heatmap
 * (`hourlyHeat.ts`) so the grid's row labels and this table's own Hour
 * column never drift apart. */
export function hourLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`
}

// ── Daily ────────────────────────────────────────────────────────────────

export const dailyColumns: ReportColumn<DailyRow>[] = [
  { key: 'date', label: 'Date', format: (r) => formatBusinessDate(r.business_date) },
  { key: 'invoices', label: 'Invoices', align: 'right', format: (r) => String(r.invoices) },
  { key: 'voids', label: 'Voids', align: 'right', format: (r) => String(r.voids) },
  { key: 'kg', label: 'Kg', align: 'right', format: (r) => formatKgGrouped(r.kg_sold) },
  { key: 'gross', label: 'Gross', align: 'right', format: (r) => formatMoney(r.gross) },
  { key: 'discount', label: 'Discount', align: 'right', format: (r) => formatMoney(r.discount) },
  { key: 'taxable', label: 'Taxable', align: 'right', format: (r) => formatMoney(r.taxable) },
  { key: 'tax', label: 'Tax', align: 'right', format: (r) => formatMoney(r.tax) },
  { key: 'further_tax', label: 'Further tax', align: 'right', format: (r) => formatMoney(r.further_tax) },
  { key: 'rounding', label: 'Rounding', align: 'right', format: (r) => formatMoney(r.rounding) },
  { key: 'net', label: 'Net', align: 'right', format: (r) => formatMoney(r.net) },
  { key: 'cash', label: 'Cash', align: 'right', format: (r) => formatMoney(r.tenders.cash) },
  { key: 'card', label: 'Card', align: 'right', format: (r) => formatMoney(r.tenders.card) },
  { key: 'online', label: 'Online', align: 'right', format: (r) => formatMoney(r.tenders.online) },
  { key: 'on_account', label: 'On account', align: 'right', format: (r) => formatMoney(r.tenders.on_account) },
  { key: 'receipts', label: 'Receipts', align: 'right', format: (r) => formatMoney(r.receipts_total) },
]

export function dailyTotals(t: PeriodSummary): Record<string, ReactNode> {
  return {
    date: 'Total',
    invoices: String(t.invoices),
    voids: String(t.voids),
    kg: formatKgGrouped(t.kg_sold),
    gross: formatMoney(t.gross),
    discount: formatMoney(t.discount),
    taxable: formatMoney(t.taxable),
    tax: formatMoney(t.tax),
    further_tax: formatMoney(t.further_tax),
    rounding: formatMoney(t.rounding),
    net: formatMoney(t.net),
    cash: formatMoney(t.tenders.cash),
    card: formatMoney(t.tenders.card),
    online: formatMoney(t.tenders.online),
    on_account: formatMoney(t.tenders.on_account),
    receipts: formatMoney(t.receipts_total),
  }
}

// ── Products ─────────────────────────────────────────────────────────────

export const productColumns: ReportColumn<ProductRow>[] = [
  { key: 'name', label: 'Product', format: (r) => r.name },
  { key: 'kg', label: 'Kg', align: 'right', format: (r) => formatKgGrouped(r.kg) },
  { key: 'invoices', label: 'Invoices', align: 'right', format: (r) => String(r.invoices) },
  { key: 'gross', label: 'Gross', align: 'right', format: (r) => formatMoney(r.gross) },
  {
    key: 'deal_variance',
    label: 'Deal variance',
    align: 'right',
    format: (r) => <span className={r.deal_variance < 0 ? 'text-destructive' : undefined}>{signedMoney(r.deal_variance)}</span>,
  },
  { key: 'share', label: 'Share %', align: 'right', format: (r) => plainPercentLabel(r.share) },
]

// The Invoices column is the one figure in this table that does not sum:
// each row is COUNT(DISTINCT invoice_id) for that product
// (backend/internal/reports/queries.go Products), so a two-product sale is
// counted once per row and the column adds up to more than the window's
// invoice count. Kg and Gross do sum exactly. Rather than put a number in
// the footer that is not the column's total — which reads as an arithmetic
// error — the cell is left as a dash; the window's real invoice count is on
// the Daily tab and in the tiles above.
export function productTotals(t: PeriodSummary): Record<string, ReactNode> {
  return {
    name: 'Total',
    kg: formatKgGrouped(t.kg_sold),
    invoices: DASH,
    gross: formatMoney(t.gross),
    share: t.gross !== 0 ? plainPercentLabel(100) : DASH,
  }
}

// ── Tax ──────────────────────────────────────────────────────────────────

export const taxColumns: ReportColumn<TaxBand>[] = [
  { key: 'rate', label: 'Rate %', align: 'right', format: (r) => percentLabel(r.rate) },
  { key: 'invoices', label: 'Invoices', align: 'right', format: (r) => String(r.invoices) },
  { key: 'taxable', label: 'Taxable', align: 'right', format: (r) => formatMoney(r.taxable) },
  { key: 'tax', label: 'Tax', align: 'right', format: (r) => formatMoney(r.tax) },
  { key: 'further_tax', label: 'Further tax', align: 'right', format: (r) => formatMoney(r.further_tax) },
  { key: 'filed', label: 'Filed', align: 'right', format: (r) => `${r.filed_count} · ${formatMoney(r.filed_amount)}` },
  { key: 'unfiled', label: 'Unfiled', align: 'right', format: (r) => `${r.unfiled_count} · ${formatMoney(r.unfiled_amount)}` },
  { key: 'rejected', label: 'Rejected', align: 'right', format: (r) => String(r.failed_count) },
]

export function taxTotals(t: PeriodSummary): Record<string, ReactNode> {
  return {
    rate: 'Total',
    invoices: String(t.invoices),
    taxable: formatMoney(t.taxable),
    tax: formatMoney(t.tax),
    further_tax: formatMoney(t.further_tax),
  }
}

// ── Cashiers ─────────────────────────────────────────────────────────────

export const cashierColumns: ReportColumn<CashierRow>[] = [
  { key: 'name', label: 'Cashier', format: (r) => r.name || DASH },
  { key: 'invoices', label: 'Invoices', align: 'right', format: (r) => String(r.invoices) },
  { key: 'gross', label: 'Gross', align: 'right', format: (r) => formatMoney(r.gross) },
  { key: 'average', label: 'Average', align: 'right', format: (r) => formatMoney(r.average) },
  { key: 'voids', label: 'Voids', align: 'right', format: (r) => String(r.voids) },
]

export function cashierTotals(t: PeriodSummary): Record<string, ReactNode> {
  const average = t.invoices > 0 ? t.gross / t.invoices : 0
  return {
    name: 'Total',
    invoices: String(t.invoices),
    gross: formatMoney(t.gross),
    average: formatMoney(average),
    voids: String(t.voids),
  }
}

// ── Hourly ───────────────────────────────────────────────────────────────

export const hourlyColumns: ReportColumn<HourRow>[] = [
  { key: 'hour', label: 'Hour', format: (r) => hourLabel(r.hour) },
  { key: 'invoices', label: 'Invoices', align: 'right', format: (r) => String(r.invoices) },
  { key: 'net', label: 'Net', align: 'right', format: (r) => formatMoney(r.net) },
]

export function hourlyTotals(t: PeriodSummary): Record<string, ReactNode> {
  return {
    hour: 'Total',
    invoices: String(t.invoices),
    net: formatMoney(t.net),
  }
}

// ── Receivables (a position — no totals row; spec §6.8) ────────────────────

/** The customer name links to the customer list (`/customers`) — the route
 * has no `?id=` deep-link to open one customer's detail directly, so the
 * link lands the owner on the list they can search from rather than a page
 * that does not exist. */
export const receivableColumns: ReportColumn<ReceivableRow>[] = [
  {
    key: 'name',
    label: 'Customer',
    format: (r) => (
      <Link to="/customers" className="font-semibold underline underline-offset-2 hover:text-primary-hover">
        {r.name}
      </Link>
    ),
  },
  { key: 'phone', label: 'Phone', format: (r) => r.phone || DASH },
  { key: 'balance', label: 'Balance', align: 'right', format: (r) => formatMoney(r.balance) },
  { key: 'b0_30', label: '0-30', align: 'right', format: (r) => formatMoney(r.b0_30) },
  { key: 'b31_60', label: '31-60', align: 'right', format: (r) => formatMoney(r.b31_60) },
  { key: 'b61_90', label: '61-90', align: 'right', format: (r) => formatMoney(r.b61_90) },
  { key: 'b90', label: '90+', align: 'right', format: (r) => formatMoney(r.b90) },
  { key: 'last_receipt', label: 'Last receipt', format: (r) => (r.last_receipt ? formatBusinessDate(r.last_receipt) : DASH) },
]

// ── Day closes (sealed rows — no totals row; spec §6.8) ─────────────────────

// The Net column here is `business_days.net_sales` as sealed by the close,
// not a live recomputation: a void entered after the close moves the Daily
// tab and never this row (a printed Z-slip cannot restate itself). Hence
// the "Net (sealed)" label — matching the export header in
// backend/internal/handlers/reports.go `dayClosesView` — and the note
// `DayClosesPanel` carries above the table.

function statusLabel(status: DayCloseRow['status']): string {
  switch (status) {
    case 'open':
      return 'Open'
    case 'reopened':
      return 'Reopened'
    case 'closed':
      return 'Closed'
    default:
      return status
  }
}

/** `onOpenZReport` is supplied by the tab, which owns the dialog's open
 * state — this module stays free of any component state of its own. */
export function dayCloseColumns(onOpenZReport: (row: DayCloseRow) => void): ReportColumn<DayCloseRow>[] {
  return [
    { key: 'date', label: 'Date', format: (r) => formatBusinessDate(r.business_date) },
    { key: 'status', label: 'Status', format: (r) => statusLabel(r.status) },
    { key: 'closed_by', label: 'Closed by', format: (r) => r.closed_by || DASH },
    { key: 'expected_cash', label: 'Expected cash', align: 'right', format: (r) => formatMoney(r.expected_cash) },
    { key: 'counted_cash', label: 'Counted cash', align: 'right', format: (r) => formatMoney(r.counted_cash) },
    { key: 'cash_variance', label: 'Cash variance', align: 'right', format: (r) => signedMoney(r.cash_variance) },
    { key: 'card_variance', label: 'Card variance', align: 'right', format: (r) => signedMoney(r.card_variance) },
    { key: 'online_variance', label: 'Online variance', align: 'right', format: (r) => signedMoney(r.online_variance) },
    { key: 'net', label: 'Net (sealed)', align: 'right', format: (r) => formatMoney(r.net) },
    {
      key: 'z_report',
      label: 'Z-report',
      align: 'right',
      format: (r) => (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation()
            onOpenZReport(r)
          }}
        >
          View
        </Button>
      ),
    },
  ]
}
